import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content
 * Uses same sanitization rules as RichTextEditor for consistency
 */
export const sanitizeHTML = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'b', 'i', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'span', 'div'],
    ALLOWED_ATTR: ['class', 'style'],
  });
};

/** Detects whether a string already contains the block/inline tags the editor renders. */
const HTML_TAG_PATTERN = /<\/?(p|br|ul|ol|li|div|strong|em|b|i|u|span)\b[^>]*>/i;

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Converts plain text into the HTML the RichTextEditor (Quill) renders.
 *
 * Quill treats its `value` as HTML and models each line as its own `<p>` block, so
 * plain text with literal `\n` line breaks collapses into a single run-on block.
 * This maps every line to a `<p>` and represents a blank line (a `\n\n` paragraph
 * break) as Quill's own empty-line markup `<p><br></p>` — without it, consecutive
 * paragraphs render with no gap and a double newline looks identical to a single
 * one. Runs of blank lines collapse to a single gap, and leading/trailing blank
 * lines are trimmed.
 *
 * Input that is already HTML (e.g. a previously saved draft) is passed through the
 * sanitizer unchanged, so the conversion is idempotent and safe to run on save.
 */
export const plainTextToHtml = (text?: string | null): string => {
  if (!text) return '';
  if (HTML_TAG_PATTERN.test(text)) return sanitizeHTML(text);

  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => escapeHtml(line.trim()));

  // Collapse runs of blank lines into a single blank so a paragraph break is one gap.
  const collapsed: string[] = [];
  lines.forEach((line) => {
    if (line === '' && collapsed[collapsed.length - 1] === '') return;
    collapsed.push(line);
  });

  // Trim leading / trailing blank lines so the text never opens or closes with a gap.
  while (collapsed[0] === '') collapsed.shift();
  while (collapsed[collapsed.length - 1] === '') collapsed.pop();

  return collapsed.map((line) => (line ? `<p>${line}</p>` : '<p><br></p>')).join('');
};
